import { useState } from 'react'
import { useAuth } from '../../contexts/auth-context'

/**
 * Shown to invited members whose account is still `PENDING_INVITE` — i.e. they
 * have set their password but a Provider Owner (L4) has not approved them yet.
 * The shared server policies already block the directory and messaging for
 * pending accounts; this is the matching UI so a pending user sees a clear
 * "waiting for approval" state instead of an empty/blocked app.
 */
export function PendingApprovalScreen() {
  const { user, signOut, refresh } = useAuth()
  const [signOutError, setSignOutError] = useState(false)

  const handleSignOut = () => {
    setSignOutError(false)
    // Fail-closed: on failure the user is still signed in — show the error
    // instead of letting the void'ed rejection vanish.
    void signOut().catch(() => setSignOutError(true))
  }

  return (
    <div className="flex min-h-dvh flex-col items-center justify-center bg-slate-50 px-6 py-12">
      <div className="w-full max-w-md rounded-2xl border border-slate-200 bg-white p-8 text-center shadow-sm">
        <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-full bg-amber-50 text-amber-600">
          <svg
            className="h-7 w-7"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden
          >
            <circle cx="12" cy="12" r="9" />
            <path d="M12 7v5l3 2" />
          </svg>
        </div>
        <h1 className="text-xl font-bold text-slate-900">Your account is pending approval</h1>
        <p className="mt-2 text-sm text-slate-600">
          Thanks{user?.givenName ? `, ${user.givenName}` : ''}! Your details are in and a Provider Owner
          is reviewing your access. You&apos;ll be able to use the app as soon as you&apos;re approved.
        </p>
        <p className="mt-4 text-xs text-slate-400">
          Signed in as {user?.email}
        </p>
        <div className="mt-6 flex flex-col gap-2">
          <button
            type="button"
            onClick={() => void refresh()}
            className="rounded-lg bg-brand-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-brand-700"
          >
            Check again
          </button>
          <button
            type="button"
            onClick={handleSignOut}
            className="rounded-lg border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50"
          >
            Sign out
          </button>
          {signOutError && (
            <p role="alert" className="text-xs font-medium text-red-600">
              Sign-out failed. Please try again.
            </p>
          )}
        </div>
      </div>
    </div>
  )
}
